import {
  buildOnrampPaymentToken,
  isInsufficientFundsPayment,
  isMintingStagePayment,
  isPendingPayment,
} from 'entities/payment';
import { NormalizedToken } from 'entities/payment/model';
import { CancelOrderButton } from 'features/payment-actions';
import { COLOR_ACCENT } from 'shared';
import { isNative } from 'shared/lib/token/constants';
import { withAwaitingPaymentResolve } from 'widgets/buy-package/lib';
import { usePendingPayment } from 'widgets/buy-package/models';
import { OnramperErrorBox } from 'widgets/buy-package/ui/payment-awaiting/payment-awaiting-debit/onramper-error-box';
import { buildOnramperUrl } from 'widgets/onramper/lib/build-onramper-url';
import { Currency, OnramperNetworkId } from 'widgets/onramper/types';
import { OnramperWidget } from 'widgets/onramper/ui';

import {
  BackgroundCard,
  DebitHint,
  DebitHintNativeToken,
  DebitWarning,
  InsufficientFundsWarning,
  MintingHint,
  PaymentCountdown,
} from '../../shared';

const getOnRamperUrl = (paymentToken: NormalizedToken) => {
  try {
    const onrampData = buildOnrampPaymentToken(paymentToken);

    return buildOnramperUrl({
      mode: 'buy',
      onlyCryptos: onrampData.onlyCryptos,
      onlyCryptoNetworks: onrampData.onlyCryptoNetworks as OnramperNetworkId[],
      defaultFiat: Currency.USD,
    });
  } catch (error) {
    return null;
  }
};

const PaymentAwaitingDebit = () => {
  const { paymentToken, payment, onExpired } = usePendingPayment();

  if (!payment || !paymentToken) return null;

  const onramperUrl = getOnRamperUrl(paymentToken);

  const isMintingStage = isMintingStagePayment(payment);

  return (
    <BackgroundCard title={isMintingStage ? 'Minting' : 'Expecting Payment'}>
      {onramperUrl === null ? (
        <OnramperErrorBox />
      ) : (
        <OnramperWidget
          url={`${onramperUrl}&primaryColor=${COLOR_ACCENT.slice(1)}`}
          height="650px"
          borderRadius="12px"
        />
      )}

      {isNative(paymentToken.address) && paymentToken.symbol.toLowerCase() !== 'btc' ? (
        <DebitHintNativeToken
          packageName={payment.payload.packageName}
          tokenSymbol={paymentToken.symbol}
          tokenAmount={payment.payload.amountToPay}
          tokenDecimals={paymentToken.decimals}
          paymentAddress={payment.payload.wallet}
          tokenNetworkShortName={paymentToken.tokenNetworkShortName}
          sx={{ mt: 3 }}
        />
      ) : (
        <DebitHint
          packageName={payment.payload.packageName}
          tokenSymbol={paymentToken.symbol}
          tokenAmount={payment.payload.amountToPay}
          tokenDecimals={paymentToken.decimals}
          paymentAddress={payment.payload.wallet}
          showHintText={!isMintingStage}
          tokenNetworkShortName={paymentToken.tokenNetworkShortName}
          sx={{ mt: 3 }}
        />
      )}

      <DebitWarning>
        Make sure that the currency selected in the Onramper matches the currency you selected on the previous screen.
        Otherwise, it may lead to loss of funds. <br />
      </DebitWarning>

      {isMintingStage && <MintingHint />}

      {!isMintingStage && <PaymentCountdown onTimeout={onExpired} endTime={payment.payload.expiresAt * 1000} mb={2} />}

      {isPendingPayment(payment) && <CancelOrderButton id={payment.payload.requestId} />}
      {isInsufficientFundsPayment(payment) && (
        <InsufficientFundsWarning
          symbol={paymentToken.symbol}
          cost={payment.payload.initialPackagePrice}
          depositedAmount={payment.payload.currentBalance}
          decimals={paymentToken.decimals}
        />
      )}
    </BackgroundCard>
  );
};

export const PaymentAwaitingDebitView = withAwaitingPaymentResolve(PaymentAwaitingDebit);
